import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { FacultyTable } from '../components/faculty';
import { useFaculty } from '../hooks/useFaculty';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, Filter, X } from 'lucide-react';
import { toast } from 'sonner';

export default function FacultyListPage() {
  const [facultyData, setFacultyData] = useState({ data: [], total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const { fetchFacultyList, deactivateFaculty, deleteFaculty, reactivateFaculty, loading } = useFaculty();
  const { isAdmin } = useAuth();

  const loadFaculty = useCallback(async () => {
    try {
      const params = { page, page_size: 10 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const data = await fetchFacultyList(params);
      setFacultyData(data);
    } catch (err) {
      toast.error('Failed to load faculty list');
    }
  }, [fetchFacultyList, page, search, statusFilter]);

  useEffect(() => {
    loadFaculty();
  }, [loadFaculty]);

  const handleDeactivate = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this faculty member?')) {
      try {
        await deactivateFaculty(id);
        toast.success('Faculty deactivated successfully');
        loadFaculty();
      } catch (err) {
        toast.error('Failed to deactivate faculty');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('This will permanently delete the faculty record. Continue?')) {
      try {
        await deleteFaculty(id);
        toast.success('Faculty deleted successfully');
        loadFaculty();
      } catch (err) {
        toast.error('Failed to delete faculty');
      }
    }
  };

  const handleReactivate = async (id) => {
    try {
      await reactivateFaculty(id);
      toast.success('Faculty reactivated successfully');
      loadFaculty();
    } catch (err) {
      toast.error('Failed to reactivate faculty');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadFaculty();
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const totalPages = Math.ceil(facultyData.total / 10);

  return (
    <DashboardLayout title="Faculty Management">
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Faculty Directory
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage all faculty members
            </p>
          </div>
          {isAdmin() && (
            <Link
              to="/admin/faculty/create"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2.5 transition-colors duration-200 font-medium"
              data-testid="add-faculty-btn"
            >
              <Plus size={20} />
              Add Faculty
            </Link>
          )}
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9 pr-8 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors appearance-none"
                  data-testid="status-filter"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {(search || statusFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  data-testid="clear-filters-btn"
                  title="Clear filters"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </form>
        </div>

        <FacultyTable 
          data={facultyData.data} 
          onDeactivate={handleDeactivate}
          onDelete={isAdmin() ? handleDelete : undefined}
          onReactivate={isAdmin() ? handleReactivate : undefined}
          viewPathPrefix={isAdmin() ? '/admin/faculty' : '/faculty/members'}
          loading={loading}
        />

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((page - 1) * 10) + 1} to {Math.min(page * 10, facultyData.total)} of {facultyData.total} results
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="prev-page-btn"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="next-page-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
